import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrderCreateRequest,
  OrderUpdateRequest,
  ResponseInstruments,
  ResponseOrder,
  ResponseOrderKV,
  ResponsePortfolios,
  ResponseRates,
  ResponseShares,
  ResponseVariableOrder,
  ResponseVariableOrderKV,
  VariableOrderCreateRequest,
  VariableOrderUpdateRequest
} from '../../domain/entities/OrdersEntity';
import { variableOrderKeys, orderRepository } from '../../infrastructure/VariableOrdersConstants';
import { orderKeys } from '../../infrastructure/OrdersConstants';

// Hook para obtener todas las órdenes
export const useGetOrders = () => {
  return useQuery<ResponseOrderKV, Error>({
    queryKey: orderKeys.all,
    queryFn: () => orderRepository.getAllOrders(),
  });
}

// Hook para obtener una orden por ID
export const useGetOrderById = (id: string) => {
  return useQuery<ResponseOrderKV, Error>({
    queryKey: orderKeys.details(id),
    queryFn: () => orderRepository.getOrderById(id),
    enabled: !!id, // Solo se ejecuta si hay un ID
  });
}

// Hook para crear una orden
export const useCreateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseOrder, Error, OrderCreateRequest>({
    mutationFn: (order) => orderRepository.createOrder(order),
    onSuccess: () => {
      // Invalida la caché de todas las órdenes para forzar una recarga
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

// Hook para actualizar una orden
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseOrder, Error, OrderUpdateRequest>({
    mutationFn: (order) => orderRepository.updateOrder(order),
    onSuccess: (_, variables) => {
      // Invalida tanto la lista completa como los detalles de la orden específica
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.details(variables.id) });
      }
    },
  });
}

// Hook para eliminar una orden
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseOrder, Error, string>({
    mutationFn: (id) => orderRepository.deleteOrder(id),
    onSuccess: (_, id) => {
      // Invalida la caché de todas las órdenes
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      // Elimina la caché de la orden específica
      queryClient.removeQueries({ queryKey: orderKeys.details(id) });
    },
  });
}

export const useGetPortfolios = () => {
  return useQuery<ResponsePortfolios, Error>({
    queryKey: orderKeys.allPortfolios,
    queryFn: () => orderRepository.getAllPortfolios(),
  });
}

export const useGetInstruments = () => {
  return useQuery<ResponseInstruments, Error>({
    queryKey: orderKeys.allInstruments,
    queryFn: () => orderRepository.getAllInstruments(),
  });
}

export const useGetRates = () => {
  return useQuery<ResponseRates, Error>({
    queryKey: orderKeys.allRates,
    queryFn: () => orderRepository.getAllRates(),
  });
}

export const useGetShares = () => {
  return useQuery<ResponseShares, Error>({
    queryKey: variableOrderKeys.allShares,
    queryFn: () => orderRepository.getAllShares(),
  });
}

// Hook para obtener todas las órdenes
export const useGetVariableOrders = () => {
  return useQuery<ResponseVariableOrderKV, Error>({
    queryKey: variableOrderKeys.all,
    queryFn: () => orderRepository.getAllVariableOrders(),
  });
}

// Hook para crear una orden
export const useCreateVariableOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseVariableOrder, Error, VariableOrderCreateRequest>({
    mutationFn: (order) => orderRepository.createVariableOrder(order),
    onSuccess: () => {
      // Invalida la caché de todas las órdenes para forzar una recarga
      queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
    },
  });
}

// Hook para actualizar una orden
export const useUpdateVariableOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseVariableOrder, Error, VariableOrderUpdateRequest>({
    mutationFn: (order) => orderRepository.updateVariableOrder(order),
    onSuccess: (_, variables) => {
      // Invalida tanto la lista completa como los detalles de la orden específica
      queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: variableOrderKeys.details(variables.id) });
      }
    },
  });
}

// Hook para eliminar una orden
export const useDeleteVariableOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<ResponseVariableOrder, Error, string>({
    mutationFn: (id) => orderRepository.deleteVariableOrder(id),
    onSuccess: (_, id) => {
      // Invalida la caché de todas las órdenes
      queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
      // Elimina la caché de la orden específica
      queryClient.removeQueries({ queryKey: variableOrderKeys.details(id) });
    },
  });
}
